import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetOnboardingConfigQuery,
  useGetTenantSettingsQuery,
  useUpdateTenantMutation,
} from '@/redux/internal-services/onboardingApi';
import { StepShell } from './StepShell';
import type { StepHandle, SubmitResult } from './stepHandle';

interface Props {
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
  disabled?: boolean;
}

/**
 * Guided-setup "Scope of Work Type" step — multi-select of the work types the business
 * performs (options come from the onboarding config). Persists to tenant.scope_of_work.
 * Exposes a {@link StepHandle} so the voice agent can toggle options and submit.
 */
const ScopeOfWorkStep = forwardRef<StepHandle, Props>(({ onBack, onNext, onSkip, disabled = false }, ref) => {
  const { t } = useTranslation();
  const { data: onboardingConfig } = useGetOnboardingConfigQuery();
  const { data: tenantSettings } = useGetTenantSettingsQuery();
  const [updateTenant, { isLoading: saving }] = useUpdateTenantMutation();

  const options = onboardingConfig?.data?.scope_of_work_types ?? [];
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState('');

  // Synchronous mirror so submit/getState read the freshest selection.
  const selectedRef = useRef<string[]>(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    const d = tenantSettings?.data as Record<string, unknown> | undefined;
    if (Array.isArray(d?.scope_of_work)) setSelected(d!.scope_of_work as string[]);
  }, [tenantSettings]);

  const toggle = (opt: string) =>
    setSelected((prev) => (prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt]));

  // Voice actuator — set a specific option on/off (case-insensitive match against options).
  const toggleOption = (opt: string, on: boolean) => {
    const canonical = options.find((o) => o.toLowerCase() === opt.toLowerCase()) ?? opt;
    const has = selectedRef.current.includes(canonical);
    let next = selectedRef.current;
    if (on && !has) next = [...selectedRef.current, canonical];
    else if (!on && has) next = selectedRef.current.filter((x) => x !== canonical);
    selectedRef.current = next;
    setSelected(next);
  };

  const persist = async (): Promise<SubmitResult> => {
    setError('');
    try {
      await updateTenant({ scope_of_work: selectedRef.current }).unwrap();
      return { ok: true };
    } catch {
      setError(t('common.failedToSave'));
      return { ok: false, reason: 'transient', message: 'Failed to save scope of work.' };
    }
  };

  const handleNext = () => { void persist().then((r) => { if (r.ok) onNext(); }); };

  useImperativeHandle(ref, (): StepHandle => ({
    getState: () => ({ selected: selectedRef.current, options }),
    submit: persist,
    toggleOption,
  }));

  return (
    <StepShell
      title="Scope of Work Type"
      subtitle="Tell us about your scope of work type"
      onBack={onBack}
      onNext={handleNext}
      onSkip={onSkip}
      disabled={disabled}
      saving={saving}
      error={error}
    >
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              selected.includes(opt)
                ? 'bg-accent-fill text-on-accent'
                : 'bg-ui-subtle text-ui-text2 hover:bg-ui-hover'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </StepShell>
  );
});

ScopeOfWorkStep.displayName = 'ScopeOfWorkStep';

export default ScopeOfWorkStep;
